import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API, fmtMoney, today, daysAgo } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, PageBody } from "@/components/common/PageHeader";
import FilterBar from "@/components/common/FilterBar";
import EmptyState from "@/components/common/EmptyState";
import PartyDrawer from "@/components/common/PartyDrawer";
import CsvImportDialog from "@/components/common/CsvImportDialog";
import { Button } from "@/components/ui/button";
import { Plus, Users, FileText, Upload, Archive, Trash2 } from "lucide-react";
import CustomerStatementDialog from "@/components/sales/CustomerStatementDialog";
import { useSortableList, SortHeader } from "@/components/common/useSortableList";
import { toast } from "sonner";

export default function Customers() {
    const { activeOrg } = useAuth();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [search, setSearch] = useState("");
    const [drawer, setDrawer] = useState({ open: false, item: null });
    const [statement, setStatement] = useState({ open: false, customer: null });
    const [importOpen, setImportOpen] = useState(false);
    const [batchOpen, setBatchOpen] = useState(false);
    const [loading, setLoading] = useState(true);

    const { sorted, sortKey, sortDir, onSort } = useSortableList(items, {
        storageKey: "customers",
        initial: { key: "name", dir: "asc" },
        search,
        searchKeys: ["name", "company", "email", "gstin"],
    });

    const load = async () => {
        setLoading(true);
        const { data } = await api.get("/customers");
        setItems(data); setLoading(false);
    };

    useEffect(() => { load(); }, [activeOrg?.id]);

    const save = async (form) => {
        if (drawer.item) await api.put(`/customers/${drawer.item.id}`, form);
        else await api.post(`/customers`, form);
        await load();
    };

    const onDelete = async (c) => {
        if (!confirm(`Delete customer "${c.name}"? This will fail if any invoice / payment is still attached.`)) return;
        try {
            await api.delete(`/customers/${c.id}`);
            toast.success(`${c.name} deleted`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not delete — customer has linked transactions");
        }
    };

    return (
        <>
            <PageHeader
                kicker="SALES"
                title="Customers"
                subtitle="Track every relationship — credit terms, GSTIN, multi-currency profile, and outstanding balances."
                actions={
                    <>
                        <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="customer-import"><Upload className="w-4 h-4 mr-1.5" />Import CSV</Button>
                        <Button variant="outline" onClick={() => setBatchOpen(true)} data-testid="customer-batch-statements"><Archive className="w-4 h-4 mr-1.5" />Batch statements</Button>
                        <Button onClick={() => setDrawer({ open: true, item: null })} data-testid="customer-new"><Plus className="w-4 h-4 mr-1.5" />New customer</Button>
                    </>
                }
            />
            <PageBody>
                <FilterBar search={search} onSearch={setSearch} />
                <div className="bg-card border border-border rounded-md overflow-hidden">
                    {loading ? (
                        <div className="p-8 text-center text-muted-foreground">Loading…</div>
                    ) : sorted.length === 0 ? (
                        <EmptyState icon={Users} title="No customers yet" description="Add your first customer to start invoicing." actionLabel="New customer" onAction={() => setDrawer({ open: true, item: null })} />
                    ) : (
                        <table className="fin-table">
                            <thead><tr>
                                <SortHeader label="Name" sortKey="name" currentKey={sortKey} dir={sortDir} onSort={onSort} />
                                <SortHeader label="GSTIN" sortKey="gstin" currentKey={sortKey} dir={sortDir} onSort={onSort} />
                                <SortHeader label="Currency" sortKey="currency" currentKey={sortKey} dir={sortDir} onSort={onSort} />
                                <SortHeader label="Terms" sortKey="payment_terms_days" currentKey={sortKey} dir={sortDir} onSort={onSort} />
                                <SortHeader label="Outstanding" sortKey="outstanding" currentKey={sortKey} dir={sortDir} onSort={onSort} align="right" />
                                <th></th>
                            </tr></thead>
                            <tbody>
                                {sorted.map((c) => (
                                    <tr key={c.id} data-testid={`customer-row-${c.id}`}>
                                        <td className="cursor-pointer" onClick={() => navigate(`/sales/customers/${c.id}`)}>
                                            <div className="font-medium hover:text-gold hover:underline underline-offset-2 decoration-gold/40" data-testid={`customer-360-link-${c.id}`}>{c.name}</div>
                                            <div className="text-[11.5px] text-muted-foreground">{c.email || c.phone || "—"}</div>
                                        </td>
                                        <td className="font-mono text-[12px] cursor-pointer" onClick={() => setDrawer({ open: true, item: c })}>{c.gstin || "—"}</td>
                                        <td className="cursor-pointer" onClick={() => setDrawer({ open: true, item: c })}>{c.currency}</td>
                                        <td className="text-[12px] text-muted-foreground cursor-pointer" onClick={() => setDrawer({ open: true, item: c })}>{c.payment_terms_label || (c.payment_terms_days ? `Net ${c.payment_terms_days}` : "Due on receipt")}</td>
                                        <td className="num cursor-pointer" onClick={() => setDrawer({ open: true, item: c })}>{fmtMoney(c.outstanding || 0, c.currency)}</td>
                                        <td className="text-right">
                                            <div className="inline-flex items-center gap-1.5">
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); setStatement({ open: true, customer: c }); }}
                                                    className="inline-flex items-center gap-1 text-[11.5px] font-medium px-2.5 py-1 rounded border border-border hover:bg-[#fafaf7] transition"
                                                    data-testid={`customer-statement-${c.id}`}
                                                    title="Statement of Account"
                                                >
                                                    <FileText className="w-3.5 h-3.5" /> Statement
                                                </button>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); onDelete(c); }}
                                                    className="inline-flex items-center justify-center w-7 h-7 rounded border border-border hover:border-destructive hover:text-destructive transition"
                                                    data-testid={`customer-delete-${c.id}`}
                                                    title="Delete customer"
                                                >
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </PageBody>
            <PartyDrawer
                open={drawer.open}
                onClose={() => setDrawer({ open: false, item: null })}
                party={drawer.item}
                kind="customer"
                onSave={save}
            />
            <CustomerStatementDialog
                open={statement.open}
                onClose={() => setStatement({ open: false, customer: null })}
                customer={statement.customer}
            />
            <CsvImportDialog
                open={importOpen}
                onClose={() => setImportOpen(false)}
                title="Import customers"
                templateUrl="/customers/import/template"
                previewUrl="/customers/import/preview"
                commitUrl="/customers/import"
                onImported={load}
                testIdPrefix="customer-csv"
            />
            <BatchStatementsDialog
                open={batchOpen}
                onClose={() => setBatchOpen(false)}
            />
        </>
    );
}

function BatchStatementsDialog({ open, onClose }) {
    const [from, setFrom] = useState(daysAgo(90));
    const [to, setTo] = useState(today());
    const [onlyOverdue, setOnlyOverdue] = useState(true);
    const [loading, setLoading] = useState(false);

    if (!open) return null;
    const download = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("vyastra_token");
            const url = `${API}/customers/statements/batch.zip?from=${from}&to=${to}&only_overdue=${onlyOverdue}`;
            const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                toast.error(err.detail || "No statements to generate");
                return;
            }
            const blob = await res.blob();
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob); a.download = `Statements_${onlyOverdue ? "overdue" : "all"}_${from}_${to}.zip`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(a.href);
            toast.success("Batch downloaded");
            onClose();
        } finally { setLoading(false); }
    };

    return (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40" onClick={onClose}>
            <div className="bg-white rounded-md shadow-xl w-full max-w-md border border-border p-5" onClick={(e) => e.stopPropagation()} data-testid="batch-statements-dialog">
                <div className="text-[10px] font-semibold tracking-[0.18em] text-[#a68a56] mb-1">BATCH EXPORT</div>
                <h3 className="text-xl font-semibold mb-4">Statements ZIP</h3>
                <p className="text-[12.5px] text-muted-foreground mb-3">Generate one PDF statement per customer with an outstanding balance, bundled into a single ZIP.</p>
                <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="text-xs font-medium">From</label>
                            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-full border border-border rounded-md h-9 px-2 text-sm mt-1" data-testid="batch-from" />
                        </div>
                        <div>
                            <label className="text-xs font-medium">To</label>
                            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-full border border-border rounded-md h-9 px-2 text-sm mt-1" data-testid="batch-to" />
                        </div>
                    </div>
                    <label className="flex items-center gap-2 text-[12.5px]">
                        <input type="checkbox" checked={onlyOverdue} onChange={(e) => setOnlyOverdue(e.target.checked)} data-testid="batch-overdue-toggle" />
                        Only customers with <b>overdue</b> invoices
                    </label>
                </div>
                <div className="flex gap-2 mt-5">
                    <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
                    <Button className="flex-1" onClick={download} disabled={loading} data-testid="batch-download">
                        {loading ? "Building ZIP…" : "Download ZIP"}
                    </Button>
                </div>
            </div>
        </div>
    );
}
